import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link } from 'react-router-dom';
import { 
  FaTruck, FaEye, FaSearch, FaFilter, FaClock, FaCheckCircle,
  FaMapMarkerAlt, FaWeightHanging, FaCalendarAlt, FaFileAlt,
  FaImage, FaFilePdf, FaExternalLinkAlt, FaHistory, FaSync,
  FaChevronDown, FaChevronUp, FaCircle, FaArrowRight
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';

// "L2 of 2 - Superlink - With Air Suspension". The leg label distinguishes the loads on a job; the
// line item wording says what is actually being carried. Together they answer "which load is this?".
const lineItemLabel = (load) => {
  const li = load?.line_item;
  const wording = li?.title || li?.description || li?.commodity || null;
  const tag = load?.leg_label || (li?.line_number ? `Line ${li.line_number}` : null);
  if (tag && wording) return `${tag} · ${wording}`;
  return tag || wording || null;
};

function ClientLoads({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [loads, setLoads] = useState([]);
  const [filteredLoads, setFilteredLoads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [user, setUser] = useState({});
  const [clientId, setClientId] = useState(null);
  const [expandedLoad, setExpandedLoad] = useState(null);
  const [timeline, setTimeline] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClientData(userData);
  }, []);

  const fetchClientData = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.clients && data.clients.length > 0) {
        const id = data.clients[0].id;
        setClientId(id);
        fetchLoads(id);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
    } finally {
      // Never leave the page spinning (e.g. if no client record is returned).
      setLoading(false);
    }
  };

  const fetchLoads = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`/client/loads?client_id=${id}&include_completed=true`);
      const data = await response.json();
      if (data.success) {
        setLoads(data.loads || []);
        setFilteredLoads(data.loads || []);
      }
    } catch (err) {
      console.error('Error fetching loads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async (loadId) => {
    if (timeline[loadId]) return;

    try {
      const response = await fetch(`/client/loads/${loadId}/timeline?client_id=${clientId}`);
      const data = await response.json();
      if (data.success) {
        setTimeline(prev => ({ ...prev, [loadId]: data.timeline }));
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
    }
  };

  const toggleExpand = (loadId) => {
    if (expandedLoad === loadId) {
      setExpandedLoad(null);
    } else {
      setExpandedLoad(loadId);
      fetchTimeline(loadId);
    }
  };

  useEffect(() => {
    let filtered = loads;
    
    if (searchTerm) {
      filtered = filtered.filter(l => 
        l.job?.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.truck?.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.job?.commodity?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(l => l.status === filterStatus);
    }
    
    if (!showCompleted) {
      filtered = filtered.filter(l => l.status !== 'completed' && l.status !== 'delivered');
    }
    
    setFilteredLoads(filtered);
  }, [searchTerm, filterStatus, showCompleted, loads]);

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-gray-100 text-gray-600',
      'onsite': 'bg-blue-100 text-blue-600',
      'loading': 'bg-yellow-100 text-yellow-600',
      'en_route': 'bg-purple-100 text-purple-600',
      'at_destination': 'bg-orange-100 text-orange-600',
      'delivered': 'bg-green-100 text-green-600',
      'completed': 'bg-green-200 text-green-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'onsite': '📍 On Site',
      'loading': '🔄 Loading',
      'en_route': '🚛 En Route',
      'at_destination': '🏁 At Destination',
      'delivered': '✅ Delivered',
      'completed': '✅ Completed'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#6B7280',
      'onsite': '#3B82F6',
      'loading': '#F59E0B',
      'en_route': '#8B5CF6',
      'at_destination': '#F97316',
      'delivered': '#00A86B',
      'completed': '#047857'
    };
    return colors[status] || '#6B7280';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusOrder = ['pending', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];
  const getStatusIndex = (status) => statusOrder.indexOf(status) + 1;

  if (loading) {
    return (
      <ClientLayout activeTab="loads" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading loads...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout activeTab="loads" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            🚛 My Loads
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {filteredLoads.length} loads • {loads.filter(l => l.status !== 'completed' && l.status !== 'delivered').length} active
          </p>
        </div>
        <button
          onClick={() => fetchLoads(clientId)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors"
          style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}
        >
          <FaSync size={14} /> Refresh
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search by job #, truck, commodity..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="onsite">On Site</option>
              <option value="loading">Loading</option>
              <option value="en_route">En Route</option>
              <option value="at_destination">At Destination</option>
              <option value="delivered">Delivered</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: '#4A5568' }}>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            Show Completed
          </label>
        </div>
      </div>

      {/* Loads List */}
      {filteredLoads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🚛</div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#0A1929' }}>No Loads Found</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>No loads match your current filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLoads.map((load) => {
            const isExpanded = expandedLoad === load.id;
            const progress = getStatusIndex(load.status);
            const totalStages = 7;
            const progressPercent = Math.round((progress / totalStages) * 100);
            const hasTracking = load.tracking_link && load.status === 'en_route';
            const hasWeighbridge = load.weighbridge_image_url || load.weighbridge_weight_mt;
            const hasPOD = load.pod_image_url || load.pod_number;

            return (
              <div key={load.id} className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                {/* Main Card */}
                <div className="p-6">
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>
                          {load.job?.job_number || 'Unknown Job'}
                        </h3>
                        <span className={getStatusBadge(load.status)}>
                          {getStatusLabel(load.status)}
                        </span>
                        {hasTracking && (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-600 rounded-full animate-pulse flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            LIVE
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm" style={{ color: '#4A5568' }}>
                        <span className="flex items-center gap-1">
                          <FaTruck size={14} /> {load.truck?.registration_number || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaWeightHanging size={14} /> {load.weighbridge_weight_mt || load.estimated_weight_mt || 'N/A'} MT
                        </span>
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={14} /> {load.job?.origin || 'N/A'} → {load.job?.destination || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FaCalendarAlt size={14} /> {formatDate(load.created_at)}
                        </span>
                      </div>
                      {/* The LINE ITEM, not the whole job description. On a job with two loads this is
                          what tells them apart ("L1 of 2 - Triaxle ..." vs "L2 of 2 - Superlink ...").
                          The full job description is in the expanded view. */}
                      {lineItemLabel(load) && (
                        <p className="text-sm mt-1 font-medium" style={{ color: '#0A1929' }}>
                          {lineItemLabel(load)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/client-load-details/${load.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
                        style={{ border: '1px solid #E8ECEF', color: '#0A1929' }}
                        title="Open the full load detail"
                      >
                        <FaEye size={12} /> View
                      </Link>
                      <button
                        onClick={() => toggleExpand(load.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors"
                        style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}
                      >
                        {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                        {isExpanded ? 'Less' : 'More'}
                      </button>
                      {hasTracking && load.tracking_link && (
                        <a
                          href={load.tracking_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white transition-colors"
                          style={{ backgroundColor: '#EF4444' }}
                        >
                          <FaExternalLinkAlt size={12} /> Track Live
                        </a>
                      )}
                      <Link
                        to="/client-live-map"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white transition-colors"
                        style={{ backgroundColor: '#00A86B' }}
                      >
                        <FaMapMarkerAlt size={12} /> Live Map
                      </Link>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1" style={{ color: '#4A5568' }}>
                      <span>Progress</span>
                      <span>{progressPercent}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%`, backgroundColor: getStatusColor(load.status) }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1" style={{ color: '#4A5568' }}>
                      <span>Pending</span>
                      <span>On Site</span>
                      <span>Loading</span>
                      <span>En Route</span>
                      <span>Destination</span>
                      <span>Delivered</span>
                      <span>Completed</span>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t p-6" style={{ borderColor: '#E8ECEF' }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column - Load Details */}
                      <div>
                        <h4 className="font-semibold mb-3" style={{ color: '#0A1929' }}>Load Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between py-1 border-b" style={{ borderColor: '#E8ECEF' }}>
                            <span style={{ color: '#4A5568' }}>Status</span>
                            <span className="font-medium" style={{ color: '#0A1929' }}>{getStatusLabel(load.status)}</span>
                          </div>
                          <div className="flex justify-between py-1 border-b" style={{ borderColor: '#E8ECEF' }}>
                            <span style={{ color: '#4A5568' }}>Truck</span>
                            <span className="font-medium" style={{ color: '#0A1929' }}>{load.truck?.registration_number || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between py-1 border-b" style={{ borderColor: '#E8ECEF' }}>
                            <span style={{ color: '#4A5568' }}>Driver</span>
                            <span className="font-medium" style={{ color: '#0A1929' }}>{load.driver?.full_name || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between py-1 border-b" style={{ borderColor: '#E8ECEF' }}>
                            <span style={{ color: '#4A5568' }}>Weight</span>
                            <span className="font-medium" style={{ color: '#0A1929' }}>{load.weighbridge_weight_mt || 'N/A'} MT</span>
                          </div>
                          <div className="flex justify-between py-1 border-b" style={{ borderColor: '#E8ECEF' }}>
                            <span style={{ color: '#4A5568' }}>Arrival Date</span>
                            <span className="font-medium" style={{ color: '#0A1929' }}>{load.arrival_date || 'N/A'}</span>
                          </div>
                          {lineItemLabel(load) && (
                            <div className="flex justify-between gap-3 py-1 border-b" style={{ borderColor: '#E8ECEF' }}>
                              <span className="flex-shrink-0" style={{ color: '#4A5568' }}>Line item</span>
                              <span className="font-medium text-right" style={{ color: '#0A1929' }}>{lineItemLabel(load)}</span>
                            </div>
                          )}
                          {/* The whole job description, expanded on request only. */}
                          {load.job?.commodity && (
                            <div className="pt-3">
                              <p className="text-xs mb-1" style={{ color: '#4A5568' }}>Job description</p>
                              <p className="text-sm whitespace-pre-line" style={{ color: '#0A1929' }}>{load.job.commodity}</p>
                            </div>
                          )}
                          {load.tracking_link && (
                            <div className="flex justify-between py-1">
                              <span style={{ color: '#4A5568' }}>Tracking</span>
                              <a 
                                href={load.tracking_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                              >
                                <FaExternalLinkAlt size={12} /> Open Link
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Weighbridge Ticket */}
                        {hasWeighbridge && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <h5 className="text-sm font-medium mb-2" style={{ color: '#0A1929' }}>⚖️ Weighbridge Ticket</h5>
                            <div className="flex items-center gap-2 text-sm">
                              <span style={{ color: '#4A5568' }}>Weight:</span>
                              <span className="font-medium" style={{ color: '#0A1929' }}>{load.weighbridge_weight_mt} MT</span>
                              {load.weighbridge_image_url && (
                                <a 
                                  href={load.weighbridge_image_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="ml-auto text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs"
                                >
                                  <FaImage size={14} /> View Ticket
                                </a>
                              )}
                            </div>
                          </div>
                        )}

                        {/* POD */}
                        {hasPOD && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <h5 className="text-sm font-medium mb-2" style={{ color: '#0A1929' }}>📄 Proof of Delivery</h5>
                            <div className="flex items-center gap-2 text-sm">
                              {load.pod_number && (
                                <span style={{ color: '#4A5568' }}>POD #{load.pod_number}</span>
                              )}
                              {load.pod_image_url && (
                                <a 
                                  href={load.pod_image_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="ml-auto text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs"
                                >
                                  <FaFilePdf size={14} /> View POD
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right Column - Timeline */}
                      <div>
                        <h4 className="font-semibold mb-3" style={{ color: '#0A1929' }}>Timeline</h4>
                        {timeline[load.id] ? (
                          <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                            {timeline[load.id].map((event, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="flex flex-col items-center">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getStatusColor(event.status) }}></div>
                                  {idx < timeline[load.id].length - 1 && (
                                    <div className="w-0.5 h-8" style={{ backgroundColor: '#E8ECEF' }}></div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{event.stage}</p>
                                  <p className="text-xs" style={{ color: '#4A5568' }}>{event.description}</p>
                                  <p className="text-xs" style={{ color: '#9CA3AF' }}>{formatDate(event.timestamp)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <div className="animate-spin text-2xl">🔄</div>
                            <p className="text-xs mt-2" style={{ color: '#4A5568' }}>Loading timeline...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ClientLayout>
  );
}

export default ClientLoads;
